import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';

import { EnvironmentService } from '@patient-ui/shared-ui/utils';

/** Static backend-required headers for every API call */
const REQUIRED_HEADERS = { Authorization: 'none' } as const;

/**
 * Available service from API
 */
export interface AvailableService {
  serviceId: number;
  serviceName: string;
  showUser: boolean;
}

/**
 * Response from availableServices API
 */
export type AvailableServicesApiResponse = AvailableService[];

/**
 * Service for fetching and caching available services.
 * The available services list is relatively static and should be cached
 * to avoid unnecessary API calls.
 */
@Injectable({
  providedIn: 'root',
})
export class AvailableServicesService {
  private cachedServices$: Observable<AvailableServicesApiResponse> | null =
    null;
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private errorSubject = new BehaviorSubject<Error | null>(null);

  constructor(
    private readonly environmentService: EnvironmentService,
    private readonly httpClient: HttpClient
  ) {}

  /**
   * Get all available services. Results are cached after first call.
   * @returns Observable of all available services
   */
  getAvailableServices(): Observable<AvailableServicesApiResponse> {
    // Return cached results if available
    if (this.cachedServices$) {
      return this.cachedServices$;
    }

    const baseUrl = this.environmentService.adminApiBaseUrl;

    if (!baseUrl) {
      const error = new Error(
        'Admin API base URL is not configured in environment'
      );
      this.errorSubject.next(error);
      return of([]);
    }

    const url = `${baseUrl}/locations/fal/availableServices`;

    this.loadingSubject.next(true);
    this.errorSubject.next(null);

    // Make API call and cache the result
    this.cachedServices$ = this.httpClient
      .get<AvailableServicesApiResponse>(url, {
        headers: REQUIRED_HEADERS,
      })
      .pipe(
        tap(() => {
          this.loadingSubject.next(false);
        }),
        catchError((error: Error) => {
          this.errorSubject.next(error);
          this.loadingSubject.next(false);
          throw error;
        }),
        // Cache the result for reuse
        shareReplay(1)
      );

    return this.cachedServices$;
  }

  /**
   * Get only services that should be shown to users (showUser: true)
   * @returns Observable of user-visible services
   */
  getUserVisibleServices(): Observable<AvailableServicesApiResponse> {
    return this.getAvailableServices().pipe(
      map((services) => services.filter((service) => service.showUser))
    );
  }

  /**
   * Get loading state
   */
  get isLoading$(): Observable<boolean> {
    return this.loadingSubject.asObservable();
  }

  /**
   * Get error state
   */
  get error$(): Observable<Error | null> {
    return this.errorSubject.asObservable();
  }

  /**
   * Clear the cache and force a fresh API call on next request
   */
  clearCache(): void {
    this.cachedServices$ = null;
    this.errorSubject.next(null);
  }
}
